import type {
  IconButtonColorScheme,
  IconButtonStyleConfig,
  Theme,
} from '@frontend/legacy-components';

import { DEFAULT_PROPS } from './const';

const COLOR_SCHEMES = {
  default: (theme) => ({
    feature: theme.colors.feature.secondary,
    hover: theme.colors.interaction.surfaceHover,
    hoverFeature: theme.colors.interaction.hover,
    active: theme.colors.interaction.surfaceActive,
    activeFeature: theme.colors.interaction.active,
  }),
  destructive: (theme) => ({
    feature: theme.colors.feature.secondary,
    hover: theme.colors.alert.surfaceError,
    hoverFeature: theme.colors.interaction.destructiveHover,
    active: theme.colors.alert.surfaceError,
    activeFeature: theme.colors.interaction.destructiveActive,
  }),
  suggestion: (theme) => ({
    feature: theme.colors.feature.secondary,
    hover: theme.colors.interaction.surfaceSuggestionHover,
    hoverFeature: theme.colors.interaction.suggestionHover,
    active: theme.colors.interaction.surfaceSuggestionActive,
    activeFeature: theme.colors.interaction.suggestionActive,
  }),
  onInteraction: (theme) => ({
    feature: theme.colors.feature.onInteraction,
    hover: theme.colors.interaction.hover,
    hoverFeature: theme.colors.feature.onInteraction,
    active: theme.colors.interaction.main,
    activeFeature: theme.colors.feature.onInteraction,
  }),
} as const satisfies {
  [key in IconButtonColorScheme]: (theme: Theme) => {
    hover: string;
    hoverFeature: string;
    active: string;
    activeFeature: string;
  };
};

const BASE_STYLE: IconButtonStyleConfig['baseStyle'] = ({
  colorScheme,
  theme,
  isIconColorReactive,
}) => {
  const scheme = COLOR_SCHEMES[colorScheme] || COLOR_SCHEMES.default;
  const schemeColors = scheme(theme);

  return {
    borderRadius: 'md',
    transition: 'all 0.2s',
    color: schemeColors.feature,
    cursor: 'pointer',
    _hover: {
      backgroundColor: schemeColors.hover,
      outline: 'none',
      ...(isIconColorReactive
        ? {
            color: schemeColors.hoverFeature,
          }
        : null),
    },
    _active: {
      backgroundColor: schemeColors.active,
      ...(isIconColorReactive
        ? {
            color: schemeColors.activeFeature,
          }
        : null),
    },
    _focusVisible: {
      boxShadow: 'focus',
    },
    _disabled: {
      bg: 'transparent',
      color: 'interaction.disabled',
      cursor: 'not-allowed',
      boxShadow: 'none',
    },
  };
};

const SIZES = {
  lg: {
    w: 4,
    h: 4,
    m: -0.5,
    fontSize: '1.5rem',
  },
  md: {
    w: 3.5,
    h: 3.5,
    m: -0.5,
    fontSize: '1.25rem',
  },
  sm: {
    w: 3,
    h: 3,
    m: -0.5,
    fontSize: '1rem',
  },
} as const satisfies IconButtonStyleConfig['sizes'];

export const ICON_BUTTON_STYLE_CONFIG: IconButtonStyleConfig = {
  baseStyle: BASE_STYLE,
  sizes: SIZES,
  defaultProps: DEFAULT_PROPS,
};
